import React, { useState, useMemo } from 'react';

import { LinkOutlined, PlusOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import ListPageHeader from '@shared/ListPageHeader';
import TableIndex from '@shared/table/TableIndex';
import type { SupportRule } from '@shared/table/TableIndex';
import { MultipleValue } from '@shared/table/Rules/component';

import { consumableService } from '@services/operator/consumableService';

interface ConsumableRow {
  id: string;
  name?: string;
  barcode?: string;
  length?: number | string;
  width?: number | string;
  height?: number | string;
  volume?: number | string;
  created_at?: string;
  [key: string]: unknown;
}

export default function ConsumablesList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [lastModified, setLastModified] = useState(0);

  const columns = useMemo(() => [
    {
      nameKey: 'models.consumable.name',
      persist: true,
      sortable: true,
      sortFieldName: 'consumables.name',
      width: '200px',
      wrap: true,
      cell: ({ id, name }: ConsumableRow) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Link to={`/consumables/${id}`} style={{ wordBreak: 'break-word' }}>
            {name}
          </Link>
          <a
            href={`/operator/consumables/${id}`}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            style={{ flexShrink: 0 }} rel="noreferrer"
          >
            <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
          </a>
        </div>
      ),
    },
    {
      nameKey: 'models.consumable.barcode',
      sortable: true,
      sortFieldName: 'consumables.barcode',
      wrap: true,
      selector: ({ barcode }: ConsumableRow) => barcode,
    },
    {
      nameKey: 'models.consumable.length',
      width: '100px',
      selector: ({ length }: ConsumableRow) => length,
    },
    {
      nameKey: 'models.consumable.width',
      width: '100px',
      selector: ({ width }: ConsumableRow) => width,
    },
    {
      nameKey: 'models.consumable.height',
      width: '100px',
      selector: ({ height }: ConsumableRow) => height,
    },
    {
      nameKey: 'models.consumable.volume',
      sortable: false,
      width: '120px',
      selector: ({ volume }: ConsumableRow) => volume,
    },
    {
      nameKey: 'common.created_at',
      sortable: true,
      sortFieldName: 'consumables.id',
      width: '120px',
      selector: ({ created_at }: ConsumableRow) => created_at,
    },
  ], []);

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('models.consumable.name'),
      component: MultipleValue,
      inputName: 'consumables.name',
      defaultPinned: true,
      createable: true,
      tooltip: {
        title: t('operator.consumables.list.filter_tooltips.name.title'),
        content: t('operator.consumables.list.filter_tooltips.name.content'),
      },
    },
    {
      label: t('models.consumable.barcode'),
      component: MultipleValue,
      inputName: 'consumables.barcode',
      createable: true,
      tooltip: {
        title: t('operator.consumables.list.filter_tooltips.barcode.title'),
        content: t('operator.consumables.list.filter_tooltips.barcode.content'),
      },
    },
  ], [t]);

  return (
    <div>
      <ListPageHeader
        items={['warehouse_settings', 'warehouse_settings.consumables']}
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/consumables/new')}
          >
            {t('common.button.new')}
          </Button>
        }
      />
      <TableIndex
        columns={columns}
        searchable={{
          match: 'prefix',
          searchFields: [
            t('models.consumable.name'),
            t('models.consumable.barcode'),
          ],
        }}
        columnsEditable={true}
        tableId='OperatorConsumablesTable'
        defaultQuery={{
          sorting_field: 'consumables.id',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        service={consumableService}
        filterHelpExample={t('operator.consumables.list.filter_help_example') as string}
        filterDocsUrl="/docs/operator/consumables/list#features"
      />
    </div>
  );
}
